import { useProductMeta } from '@/hooks/meta/useProductMeta';
import {
  createWritingGoal,
  createWritingSession,
  deleteGoal,
  getGoals,
  getYearlyWritingActivity,
  getProjectProgressSummary,
  updateGoalDeadline,
  updateGoalProgress
} from '@/services/progress-tracker/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// Define cache times - using the same aggressive caching as other queries
const HOUR_IN_MS = 1000 * 60 * 60;
const DAY_IN_MS = HOUR_IN_MS * 24;
const STALE_TIME = DAY_IN_MS * 30; // 30 days stale time
const GC_TIME = DAY_IN_MS * 60; // 60 days garbage collection

// Define query keys
export const progressTrackerKeys = {
  all: ['progressTracker'] as const,
  goals: {
    all: () => [...progressTrackerKeys.all, 'goals'] as const,
    byId: (id: string) => [...progressTrackerKeys.goals.all(), id] as const,
  },
  sessions: {
    all: () => [...progressTrackerKeys.all, 'sessions'] as const,
    byProject: (projectId: string) => [...progressTrackerKeys.sessions.all(), 'project', projectId] as const,
    byStory: (storyId: string) => [...progressTrackerKeys.sessions.all(), 'story', storyId] as const,
  },
  activity: {
    all: () => [...progressTrackerKeys.all, 'activity'] as const,
    byYear: (year: number) => [...progressTrackerKeys.activity.all(), 'year', year] as const,
  },
  stats: {
    all: () => [...progressTrackerKeys.all, 'stats'] as const,
    streak: () => [...progressTrackerKeys.stats.all(), 'streak'] as const,
    longestStreak: () => [...progressTrackerKeys.stats.all(), 'longestStreak'] as const,
    writingStats: () => [...progressTrackerKeys.stats.all(), 'writingStats'] as const,
    summary: (projectId: string) => [...progressTrackerKeys.stats.all(), 'summary', projectId] as const,
  },
};

// Goals hooks
export function useGoals(projectId?: string, options?: { enabled?: boolean }) {
  return useQuery({
    // Include projectId directly in the query key for better cache management
    queryKey: [...progressTrackerKeys.goals.all(), { projectId }],
    queryFn: () => getGoals(projectId),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: options?.enabled !== undefined ? options.enabled : true,
  });
}

// Sessions hooks

// Activity hooks
export function useYearlyActivity(year: number, projectId?: string) {
  const { user } = useProductMeta();
  const userId = user?.id;

  return useQuery({
    queryKey: progressTrackerKeys.activity.byYear(year),
    queryFn: () => getYearlyWritingActivity(year, userId || undefined, projectId),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: !!userId,
    // This query depends on the user ID and projectId
    queryKeyHashFn: (queryKey) => {
      // Include userId and projectId in the query key hash
      return JSON.stringify([...queryKey, userId, projectId]);
    },
  });
}

export function useProjectProgressSummary(projectId?: string) {
  return useQuery({
    queryKey: progressTrackerKeys.stats.summary(projectId || ''),
    queryFn: () => getProjectProgressSummary(projectId || ''),
    enabled: !!projectId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useChapterStats(projectId: string) {
  return useQuery({
    queryKey: [...progressTrackerKeys.all, 'chapterStats', projectId],
    queryFn: () => import('@/services/progress-tracker/api').then(mod => mod.getProjectChapterStats(projectId)),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10,
  });
}

// Goal mutations
export function useCreateGoal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createWritingGoal,
    onSuccess: () => {
      // Only invalidate goals query
      queryClient.invalidateQueries({ queryKey: progressTrackerKeys.goals.all() });
    },
  });
}

export function useUpdateGoalProgress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, completed }: { id: string; completed: boolean; projectId?: string }) =>
      updateGoalProgress(id, completed),
    // Use optimistic updates
    onMutate: async (variables) => {
      const { id, completed, projectId } = variables;

      // Create the query key with the projectId
      const queryKey = [...progressTrackerKeys.goals.all(), { projectId }];

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousGoals = queryClient.getQueryData(queryKey);

      // Optimistically update to the new value
      queryClient.setQueryData(queryKey, (old: any[]) => {
        return old ? old.map(goal =>
          goal.id === id
            ? {
              ...goal,
              completed,
              completed_date: completed ? new Date().toISOString() : null
            }
            : goal
        ) : [];
      });

      // Return a context object with the snapshotted value
      return { previousGoals, queryKey };
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (err, _variables, context) => {
      console.error('Error updating goal progress:', err);
      if (context?.previousGoals && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousGoals);
      }
    },
    // Always refetch after error or success to ensure cache is in sync with server
    onSettled: () => {
      // We don't need to invalidate the query here since we've already updated the cache
    },
  });
}

export function useUpdateGoalDeadline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, deadline }: { id: string; deadline: string; projectId?: string }) =>
      updateGoalDeadline(id, deadline),
    // Use optimistic updates
    onMutate: async (variables) => {
      const { id, deadline, projectId } = variables;

      // Create the query key with the projectId
      const queryKey = [...progressTrackerKeys.goals.all(), { projectId }];

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousGoals = queryClient.getQueryData(queryKey);

      // Optimistically update to the new value
      queryClient.setQueryData(queryKey, (old: any[]) => {
        return old ? old.map(goal =>
          goal.id === id
            ? {
              ...goal,
              deadline
            }
            : goal
        ) : [];
      });

      // Return a context object with the snapshotted value
      return { previousGoals, queryKey };
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (err, _variables, context) => {
      console.error('Error updating goal deadline:', err);
      if (context?.previousGoals && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousGoals);
      }
    },
    // Always refetch after error or success to ensure cache is in sync with server
    onSettled: () => {
      // We don't need to invalidate the query here since we've already updated the cache
    },
  });
}

export function useDeleteGoal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { id: string; projectId?: string }) => deleteGoal(variables.id),
    // Use optimistic updates to immediately update the UI
    onMutate: async (variables) => {
      const { id: deletedGoalId, projectId } = variables;

      // Create the query key with the projectId
      const queryKey = [...progressTrackerKeys.goals.all(), { projectId }];

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousGoals = queryClient.getQueryData(queryKey);

      // Optimistically update to the new value
      queryClient.setQueryData(queryKey, (old: any[]) => {
        return old ? old.filter(goal => goal.id !== deletedGoalId) : [];
      });

      // Return a context object with the snapshotted value
      return { previousGoals, queryKey };
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (err, _variables, context) => {
      console.error('Error deleting goal:', err);
      if (context?.previousGoals && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousGoals);
      }
    },
    // Always refetch after error or success to ensure cache is in sync with server
    onSettled: () => {
      // We don't need to invalidate the query here since we've already updated the cache
      // This prevents unnecessary refetches
    },
  });
}

// Session mutations
export function useCreateSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createWritingSession,
    onSuccess: (data) => {
      // Invalidate sessions for the related project and story
      if (data && data.story_id) {
        queryClient.invalidateQueries({
          queryKey: progressTrackerKeys.sessions.byStory(data.story_id.toString())
        });

        // Also invalidate project-level sessions
        const projectId = data.project_id?.toString();
        if (projectId) {
          queryClient.invalidateQueries({
            queryKey: progressTrackerKeys.sessions.byProject(projectId)
          });
        }
      }

      // Invalidate activity data for the current year
      const currentYear = new Date().getFullYear();
      queryClient.invalidateQueries({
        queryKey: progressTrackerKeys.activity.byYear(currentYear)
      });

      // Invalidate stats
      queryClient.invalidateQueries({ queryKey: progressTrackerKeys.stats.all() });
    },
  });
}


